/**
 * Composable for fetching Yotpo reviews and bottomline data
 * Provides a clean interface for components to interact with Yotpo API
 */

export type YotpoReview = {
  id: number;
  score: number;
  votes_up: number;
  votes_down: number;
  content: string;
  title: string;
  created_at: string;
  verified_buyer: boolean;
  sentiment?: number; // Yotpo sentiment score
  is_incentivized?: boolean; // Incentivized review
  user: {
    display_name: string;
    user_type: string;
  };
  images_data?: Array<{
    original_url: string;
    thumb_url: string;
  }>;
  comment?: {
    content: string;
    created_at: string;
  };
  custom_fields?: {
    [key: string]: {
      field_type: string;
      form_id: number;
      title: string;
      value: number; // 1=smaller than expected, 2=as expected, 3=larger than expected
    };
  };
};

export type YotpoBottomline = {
  average_score: number;
  total_reviews: number;
  star_distribution?: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
  custom_fields_bottomline?: {
    [key: string]: {
      [value: string]: number; // e.g., "1": 15, "2": 120, "3": 15
    };
  };
};

export type YotpoReviewsResponse = {
  success: boolean;
  sku: string;
  data: {
    response: {
      bottomline: YotpoBottomline;
      reviews: YotpoReview[];
      pagination: {
        page: number;
        per_page: number;
        total: number;
      };
    };
  };
};

export type YotpoBottomlineResponse = {
  success: boolean;
  sku: string;
  data: {
    response: {
      bottomline: YotpoBottomline;
    };
  };
};

export type YotpoQuestion = {
  id: number;
  content: string;
  created_at: string;
  asker?: {
    id: number;
    display_name: string;
    slug: string;
    score: number;
  };
  sorted_public_answers: Array<{
    id: number;
    content: string;
    created_at: string;
    user_name: string;
    user_type: string;
  }>;
};

export type YotpoQuestionsResponse = {
  success: boolean;
  sku: string;
  data: {
    questions: YotpoQuestion[];
  };
};

export type CreateReviewInput = {
  sku: string;
  product_title: string;
  product_url: string;
  display_name: string;
  email: string;
  review_content: string;
  review_title: string;
  review_score: number;
  product_description?: string;
  product_image_url?: string;
  size_fit?: 1 | 2 | 3; // 1=smaller, 2=as expected, 3=larger
};

export type CreateQuestionInput = {
  sku: string;
  product_title: string;
  product_url: string;
  display_name: string;
  email: string;
  question_content: string;
  product_description?: string;
  product_image_url?: string;
};

export const useYotpo = () => {
  /**
   * Fetch reviews for a product
   * @param sku - Product SKU
   * @param options - Query options (per_page, page, star, sort, direction)
   */
  const fetchReviews = async(
    sku: string,
    options?: {
      per_page?: number;
      page?: number;
      star?: number;
      sort?: 'date' | 'votes_up' | 'votes_down' | 'time' | 'rating' | 'reviewer_type';
      direction?: 'desc' | 'asc';
    }
  ): Promise<YotpoReviewsResponse | null> => {
    try {
      const query = new URLSearchParams();
      
      if (options?.per_page) query.append('per_page', String(options.per_page));
      if (options?.page) query.append('page', String(options.page));
      if (options?.star) query.append('star', String(options.star));
      if (options?.sort) query.append('sort', options.sort);
      if (options?.direction) query.append('direction', options.direction);

      const queryString = query.toString();
      const url = `/api/yotpo/reviews/${encodeURIComponent(sku)}${queryString ? `?${queryString}` : ''}`;

      const data = await $fetch<YotpoReviewsResponse>(url);
      return data;
    } catch (error) {
      console.error('[useYotpo] Error fetching reviews:', error);
      return null;
    }
  };

  /**
   * Fetch bottomline (summary) for a product
   * @param sku - Product SKU
   */
  const fetchBottomline = async(sku: string): Promise<YotpoBottomlineResponse | null> => {
    try {
      const data = await $fetch<YotpoBottomlineResponse>(
        `/api/yotpo/bottomline/${encodeURIComponent(sku)}`
      );
      return data;
    } catch (error) {
      console.error('[useYotpo] Error fetching bottomline:', error);
      return null;
    }
  };

  /**
   * Fetch all reviews for a product (handles pagination automatically)
   * @param sku - Product SKU
   * @param options - Query options (star, sort, direction)
   */
  const fetchAllReviews = async(
    sku: string,
    options?: {
      star?: number;
      sort?: 'date' | 'votes_up' | 'votes_down' | 'time' | 'rating' | 'reviewer_type';
      direction?: 'desc' | 'asc';
    }
  ): Promise<YotpoReview[]> => {
    try {
      const allReviews: YotpoReview[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetchReviews(sku, {
          ...options,
          per_page: 150,
          page,
        });

        if (response && response.success) {
          const reviews = response.data.response.reviews;
          allReviews.push(...reviews);

          const pagination = response.data.response.pagination;
          hasMore = page * pagination.per_page < pagination.total;
          page++;
        } else {
          hasMore = false;
        }
      }

      return allReviews;
    } catch (error) {
      console.error('[useYotpo] Error fetching all reviews:', error);
      return [];
    }
  };

  /**
   * Create a new review for a product
   * @param input - Review data
   */
  const createReview = async(input: CreateReviewInput) => {
    try {
      const response = await $fetch('/api/yotpo/reviews/create', {
        method: 'POST',
        body: input,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('[useYotpo] Error creating review:', error);
      return {
        success: false,
        error,
      };
    }
  };

  /**
   * Fetch Q&A for a product
   * @param sku - Product SKU
   */
  const fetchQuestions = async(sku: string): Promise<YotpoQuestionsResponse | null> => {
    try {
      const data = await $fetch<YotpoQuestionsResponse>(
        `/api/yotpo/questions/${encodeURIComponent(sku)}`
      );
      return data;
    } catch (error) {
      console.error('[useYotpo] Error fetching questions:', error);
      return null;
    }
  };

  /**
   * Create a new question for a product
   * @param input - Question data
   */
  const createQuestion = async(input: CreateQuestionInput) => {
    try {
      const response = await $fetch('/api/yotpo/questions/create', {
        method: 'POST',
        body: input,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('[useYotpo] Error creating question:', error);
      return {
        success: false,
        error,
      };
    }
  };

  /**
   * Translate a review to target language
   * @param review - Review to translate
   * @param targetLang - Target language (sv or fi)
   */
  const translateReview = async(review: YotpoReview, targetLang: 'sv' | 'fi') => {
    try {
      const response = await $fetch('/api/yotpo/translate-review', {
        method: 'POST',
        body: {
          reviewId: review.id,
          title: review.title,
          content: review.content,
          targetLang,
        },
      });

      return {
        success: true,
        translatedTitle: response.translatedTitle,
        translatedContent: response.translatedContent,
        sourceLanguage: response.sourceLanguage,
      };
    } catch (error) {
      console.error('[useYotpo] Error translating review:', error);
      return {
        success: false,
        error,
      };
    }
  };

  /**
   * Translate Q&A content to target language
   * @param id - Question or answer ID
   * @param content - Content to translate
   * @param targetLang - Target language (sv or fi)
   * @param type - Type of content (question or answer)
   */
  const translateQA = async(
    id: number,
    content: string,
    targetLang: 'sv' | 'fi',
    type: 'question' | 'answer'
  ) => {
    try {
      const response = await $fetch('/api/yotpo/translate-qa', {
        method: 'POST',
        body: {
          id,
          content,
          targetLang,
          type,
        },
      });

      return {
        success: true,
        translatedContent: response.translatedContent,
        sourceLanguage: response.sourceLanguage,
      };
    } catch (error) {
      console.error('[useYotpo] Error translating Q&A:', error);
      return {
        success: false,
        error,
      };
    }
  };

  return {
    fetchReviews,
    fetchBottomline,
    fetchAllReviews,
    createReview,
    fetchQuestions,
    createQuestion,
    translateReview,
    translateQA,
  };
};
